import { AUTH_SESSION_STATUS } from "@repo/shared";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { ApiClient } from "../api-client";
import { queryClient } from "../queryClient";

import { authSessionQueryOptions } from "./useAuthSession";

import type { CurrentUserResponse } from "../generated-api";

export const currentUserQueryOptions = queryOptions({
  queryKey: ["currentUser"],
  queryFn: async () => {
    try {
      const session = await queryClient.fetchQuery(authSessionQueryOptions);
      if (session?.status !== AUTH_SESSION_STATUS.AUTHENTICATED) return null;
      const response = await ApiClient.api.authControllerCurrentUser();

      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 401) {
        return null;
      }
      throw error;
    }
  },
  staleTime: 1000 * 60 * 5,
});

export function useCurrentUser() {
  return useQuery({
    ...currentUserQueryOptions,
    select: (data: CurrentUserResponse | null) => data?.data,
  });
}

export function useCurrentUserSuspense() {
  return useSuspenseQuery({
    ...currentUserQueryOptions,
    select: (data: CurrentUserResponse | null) => {
      if (!data) {
        throw new Error("User not authenticated");
      }

      return data.data;
    },
  });
}
